import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { OverlayHelper } from '../../../../helper/overlay.helper';
import { LeadData, LeadDocumentsModel } from '../../../../models/leads/leads.model';
import { DateTimeHelper } from '../../../../helper/date-time.helper';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { UploadHelper } from '../../../../helper/uploadHelper';
import { DownloadHelper } from '../../../../helper/download.helper';
import { ClipBoardHelper } from '../../../../helper/clipboard.helper';
import { BaseCustomDialog } from '../../../shared-module/dialogs/base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CONTACTS_DATA_SERVICE, ContactsDataService } from '../../../../services/contacts/contacts-data.service';
import { LOCALE_KEY } from '../../../../services/user-service/user-token.service';
import { HideLoader, ShowLoader } from "../../../store/loader/loader.actions";
import { Store } from "@ngxs/store";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

@Component({
  selector: 'fi-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss'],
  standalone: false,
})
export class AttachmentsComponent extends BaseCustomDialog implements OnDestroy, OnChanges {
  @Input() show: boolean;
  @Output() popupClosed = new EventEmitter<void>();

  documents: LeadDocumentsModel[];
  dateTimeHelper = DateTimeHelper;
  uploadHelper = UploadHelper;
  downloadHelper = DownloadHelper;
  clipboardHelper = ClipBoardHelper;
  activeMenuIndex: number = null;
  documentsToShow: LeadDocumentsModel[];
  originalDocuments: LeadDocumentsModel[];
  sortDirection: 'ASC' | 'DESC' | 'NONE' = 'ASC';
  sortColumn: 'TYPE' | 'NAME' | 'SIZE' | 'LEAD_NUMBER' | 'DATE' | 'SENDER' = 'LEAD_NUMBER';
  arrowRight = false;
  leadMap: Map<number, LeadData>;

  private readonly leadId: number;

  constructor(
    @Inject(LEADS_DATA_SERVICE) public leadDataService: LeadsDataService,
    public dialogRef: MatDialogRef<AttachmentsComponent>,
    @Inject(CONTACTS_DATA_SERVICE) private contactsDataService: ContactsDataService,
    @Inject(MAT_DIALOG_DATA) public dialogData: {
      documents: LeadDocumentsModel[],
      leadMap: Map<number, LeadData>,
      leadId: number
    },
    private store: Store,
    public sanitizer: DomSanitizer
  ) {
    super(dialogRef);

    if ( dialogData ) {
      this.leadId = this.dialogData.leadId;
      this.documents = this.dialogData.documents;
      this.leadMap = this.dialogData.leadMap;
      this.documents?.forEach(doc => {
        doc.LeadNumber = this.leadMap?.get(doc.LeadId)?.Lead.Number ?? '';
      });
      OverlayHelper.fixBody();
      this.documentsToShow = this.documents;
      this.originalDocuments = this.documentsToShow;
      this.sortDocumentsByFavorites();
    }
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes.show?.currentValue ) {
      OverlayHelper.fixBody();
    } else {
      OverlayHelper.unfixBody();
    }

    this.documentsToShow = this.documents;
    this.originalDocuments = this.documentsToShow;
    this.sortDocumentsByFavorites();
  }

  ngOnDestroy(): void {
    OverlayHelper.unfixBody();
  }

  onClosePopup(): void {
    this.popupClosed.emit();
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  onOverlayClick( event: Event ): void {
    if ( event.target === event.currentTarget ) {
      this.onClosePopup();
    }
  }

  openMenu( $event: MouseEvent, i: number ): void {
    $event.stopPropagation();
    this.activeMenuIndex = this.activeMenuIndex !== null ? null : i;
  }

  showOtherColumns( table: HTMLElement ): void {
    this.arrowRight = !this.arrowRight;

    if ( this.arrowRight ) {
      const maxScrollLeft = table.scrollWidth - table.clientWidth;
      table.scroll({ left: maxScrollLeft, behavior: 'smooth' });
    } else {
      table.scroll({ left: 0, behavior: 'smooth' });
    }
  }

  scrollTable( table: HTMLElement ): void {
    const maxScrollLeft = table.scrollWidth - table.clientWidth;
    this.arrowRight = table.scrollLeft >= maxScrollLeft;
  }

  sortByColumn( sortColumn: 'TYPE' | 'NAME' | 'SIZE' | 'LEAD_NUMBER' | 'DATE' | 'SENDER' = 'LEAD_NUMBER' ): void {
    if ( this.sortColumn === sortColumn ) {
      switch ( this.sortDirection ) {
        case 'ASC':
          this.sortDirection = 'DESC';
          break;
        case 'DESC':
          this.sortDirection = 'NONE';
          break;
        case 'NONE':
          this.sortDirection = 'ASC';
          break;
      }
    } else {
      this.sortDirection = 'ASC';
      this.sortColumn = sortColumn;
    }

    this.compareDocuments();
  }

  compareDocuments(): void {
    this.documentsToShow = [...this.documents];

    if ( this.sortDirection !== 'NONE' ) {
      let compare: ( a: LeadDocumentsModel, b: LeadDocumentsModel ) => number;

      switch ( this.sortColumn ) {
        case 'TYPE':
          compare = ( a, b ) => a.MimeType > b.MimeType ? 1 : -1;
          break;
        case 'NAME':
          compare = ( a, b ) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1;
          break;
        case 'SIZE':
          compare = ( a, b ) => a.MimeType > b.MimeType ? 1 : -1;
          break;
        case 'LEAD_NUMBER':
          compare = ( a, b ) => a.LeadNumber > b.LeadNumber ? 1 : -1;
          break;
        case 'DATE':
          compare = ( a, b ) => a.Filedate > b.Filedate ? 1 : -1;
          break;
        case 'SENDER':
          compare = ( a, b ) => a.Creator.toLowerCase() > b.Creator.toLowerCase() ? 1 : -1;
          break;
      }

      this.documentsToShow.sort(compare);

      if ( this.sortDirection === 'DESC' ) {
        this.documentsToShow.reverse();
      }
    }
    this.originalDocuments = this.documentsToShow;
    this.sortDocumentsByFavorites();
  }

  deleteDocuments(): void {
    this.documentsToShow?.forEach(doc => {
      if ( !doc.Deleted ) {
        this.deleteDocument(doc);
      }
    })
  }

  downloadAll(): void {
    this.downloadHelper.downloadDocuments(this.leadId, this.leadDataService);
  }

  deleteDocument( document: LeadDocumentsModel ): void {
    this.leadDataService.removeLeadDocument(document.Id).subscribe(( { success } ) => {
      if ( success ) {
        document.Deleted = true;
      }
    });
  }

  getLanguage(): string {
    return sessionStorage.getItem(LOCALE_KEY);
  }

  sortDocumentsByFavorites(): void {
    const favorites = this.documentsToShow.filter(item => item.Favorite);
    const notFavorites = this.documentsToShow.filter(item => !item.Favorite);
    this.documentsToShow = favorites.concat(notFavorites);
  }

  mark( doc: LeadDocumentsModel ): void {
    this.leadDataService.markLeadDocument(doc.Id, true).subscribe(( { success, data } ) => {
      if ( success ) {
        doc.Favorite = true;
        this.documentsToShow = this.originalDocuments;
        this.sortDocumentsByFavorites();
      }
    });
  }

  unmark( doc: LeadDocumentsModel ): void {
    this.leadDataService.markLeadDocument(doc.Id, false).subscribe(( { success, data } ) => {
      if ( success ) {
        doc.Favorite = false;
        this.documentsToShow = this.originalDocuments;
        this.sortDocumentsByFavorites();
      }
    });
  }

  openPDF( doc: LeadDocumentsModel ): void {
    this.store.dispatch(new ShowLoader());

    this.downloadHelper.downloadDocument(doc, this.leadDataService, true);

    DownloadHelper.downloadFinished.subscribe(finished => {
      if ( finished ) {
        this.store.dispatch(new HideLoader());
      }
    })
  }

  getImage( ThumbnailURL: string ): string | SafeUrl {
    if ( ThumbnailURL?.includes('data:image/svg') ) {
      let data = ThumbnailURL.split(';base64')[1];
      if ( ThumbnailURL?.includes('data:image/svg+xml') ) {
        data = data.replace('svg+xml', 'svg');
      }
      data = 'data:image/svg;base64' + data;

      return this.sanitizer.bypassSecurityTrustUrl(data);
    } else {
      return ThumbnailURL
    }
  }
}
